/**
 * The one sidebar — now a list of things to do, not a list of modules.
 *
 * What was here was the whole product, faithfully: two hundred and some
 * entries, every one of them correctly named after the part of the system it
 * belongs to. It was honest and it was unusable, because the person opening
 * it does not know which module a receipt was built in. They know they were
 * handed some money, or that they have arrived at work.
 *
 * So the top of the sidebar is the same short list of doings the home screen
 * offers, in the same words, and under it the handful of lists people
 * genuinely browse - customers, products, what is owed. The whole tree is
 * still here behind "All screens", for the accountant who knows exactly where
 * they are going and for everything the task list does not cover; it is
 * ordered by the work — Sales, Purchases, Stock & production, Accounts,
 * Field sales, People — rather than by which module built the screen.
 *
 * The filtering rules have not changed: an item the user has no permission
 * for is not rendered, an item whose screen does not exist is not rendered,
 * and an empty group disappears with its heading.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChartNoAxesCombined, ChevronDown, ChevronsLeft, ChevronsRight, Home, LayoutList } from 'lucide-react';
import { NAVIGATION, isEverydayNav, navPathname, type NavItem, type NavSection } from '@rsc/shared';
import { cn } from '@/lib/utils';
import { usePermissions, useSession } from '@/stores/session';
import { navIcon } from './icons';
import { TASKS, type Task } from '@/modules/do/tasks';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** On small screens the sidebar is a drawer; selecting an item closes it. */
  onNavigate?: () => void;
}

const ALL_SCREENS_KEY = 'rsc.sidebarAllScreens';

/** Is this leaf the current page? Query strings count, so Inward and Outward registers differ. */
function leafActive(item: NavItem, pathname: string, search: string): boolean {
  if (!item.path) return false;
  const base = navPathname(item.path);
  const query = item.path.slice(base.length);
  if (base === '/') return pathname === '/';
  const pathMatches = pathname === base || (!!item.matchPrefix && pathname.startsWith(`${base}/`));
  if (!pathMatches) return false;
  if (!query) return true;
  const want = new URLSearchParams(query);
  const have = new URLSearchParams(search);
  for (const [k, v] of want) if (have.get(k) !== v) return false;
  return true;
}

function anyActive(item: NavItem, pathname: string, search: string): boolean {
  if (leafActive(item, pathname, search)) return true;
  return (item.children ?? []).some((c) => anyActive(c, pathname, search));
}

export function Sidebar({ collapsed, onToggleCollapsed, onNavigate }: SidebarProps) {
  const { canAccess } = usePermissions();
  const simple = useSession((s) => s.user?.ui_mode) === 'BEGINNER';
  const { pathname, search } = useLocation();

  /*
   * "All screens" stays open once somebody opens it, because the person who
   * wants the module tree wants it every day, and the person who does not
   * never touches it.
   */
  const [allOpen, setAllOpen] = useState(() => {
    try { return localStorage.getItem(ALL_SCREENS_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(ALL_SCREENS_KEY, allOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [allOpen]);

  const tasks = useMemo(() => TASKS.filter((t) => !t.module || canAccess(t.module)), [canAccess]);

  /** Only what this user can actually open — and in Simple view, only the everyday screens. */
  const sections = useMemo(() => {
    const visible = (item: NavItem): NavItem | null => {
      if (item.status !== 'ready') return null;
      if (simple && !isEverydayNav(item)) return null;

      if (item.children?.length) {
        const children = item.children.map(visible).filter((c): c is NavItem => c !== null);
        return children.length ? { ...item, children } : null;
      }

      if (item.module && !canAccess(item.module)) return null;
      return item.path ? item : null;
    };

    return NAVIGATION
      .map((section: NavSection) => ({
        ...section,
        items: section.items.map(visible).filter((i): i is NavItem => i !== null),
      }))
      .filter((section) => section.items.length > 0);
  }, [canAccess, simple]);

  const band = (group: Task['group']) => tasks.filter((t) => t.group === group);

  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200',
        collapsed ? 'w-[64px]' : 'w-[260px]',
      )}
    >
      <SidebarBrand collapsed={collapsed} onToggleCollapsed={onToggleCollapsed} />

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-6">
        {/* ---- Home and the figures, always first and always here --------- */}
        <ul className="space-y-0.5 pt-3">
          <FlatRow
            to="/" label="Home" icon={Home} collapsed={collapsed}
            current={pathname === '/'} onNavigate={onNavigate}
          />
          {canAccess('dashboard') && (
            <FlatRow
              to="/dashboard" label="Business" icon={ChartNoAxesCombined} collapsed={collapsed}
              current={pathname === '/dashboard'} onNavigate={onNavigate}
            />
          )}
        </ul>

        {/* Four bands, one heading word each. The band a person lives in is
            decided by what they may do, so most people see one or two. */}
        <Band label="Work" collapsed={collapsed}>
          {band('work').map((t) => (
            <TaskRow key={t.id} task={t} collapsed={collapsed} pathname={pathname} onNavigate={onNavigate} />
          ))}
        </Band>

        <Band label="Approve" collapsed={collapsed}>
          {band('approve').map((t) => (
            <TaskRow key={t.id} task={t} collapsed={collapsed} pathname={pathname} onNavigate={onNavigate} />
          ))}
        </Band>

        <Band label="Staff" collapsed={collapsed}>
          {band('staff').map((t) => (
            <TaskRow key={t.id} task={t} collapsed={collapsed} pathname={pathname} onNavigate={onNavigate} />
          ))}
        </Band>

        <Band label="Mine" collapsed={collapsed}>
          {band('me').map((t) => (
            <TaskRow key={t.id} task={t} collapsed={collapsed} pathname={pathname} onNavigate={onNavigate} />
          ))}
        </Band>

        {/* ---- The whole product, for whoever wants it -------------------- */}
        {!collapsed && sections.length > 0 && (
          <div className="mt-5 border-t border-sidebar-border pt-3">
            <button
              type="button"
              onClick={() => setAllOpen((v) => !v)}
              aria-expanded={allOpen}
              className="flex w-full items-center gap-2 rounded-[4px] px-3 py-[7px] text-[14px] font-medium text-ink-500 transition-colors hover:bg-ink-100/60 hover:text-foreground dark:text-ink-400 dark:hover:bg-ink-800/60"
            >
              <LayoutList className="h-[17px] w-[17px] shrink-0" aria-hidden />
              <span className="flex-1 text-left">All screens</span>
              <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', allOpen && 'rotate-180')} />
            </button>

            {allOpen && sections.map((section) => (
              <section key={section.label} className="mb-1">
                <h2 className="px-3 pb-1.5 pt-5 text-[12.5px] font-semibold uppercase tracking-[0.06em] text-ink-400">
                  {section.label}
                </h2>
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <SidebarRow
                      key={item.path ?? item.label}
                      item={item}
                      collapsed={false}
                      pathname={pathname}
                      search={search}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* The build this browser is actually running. When someone is told a
          change is live and does not see it, this settles in one glance
          whether the new release reached them or their browser kept the old
          one. */}
      {!collapsed && (
        <p className="shrink-0 border-t border-sidebar-border px-4 py-2 text-[11px] text-ink-400">
          Version {__BUILD_STAMP__}
        </p>
      )}
    </nav>
  );
}

/* ------------------------------------------------------------------------- */
function Band({ label, collapsed, children }: { label: string; collapsed: boolean; children: React.ReactNode }) {
  const list = Array.isArray(children) ? children.filter(Boolean) : children;
  if (Array.isArray(list) && list.length === 0) return null;
  return (
    <section className="mt-1">
      {collapsed
        ? <div className="my-3 border-t border-sidebar-border" aria-hidden />
        : (
          // Quiet enough to be a heading, not loud enough to be read first.
          <h2 className="px-3 pb-2 pt-6 text-[12.5px] font-semibold uppercase tracking-[0.06em] text-ink-400">
            {label}
          </h2>
        )}
      <ul className="space-y-0.5">{children}</ul>
    </section>
  );
}

/*
 * Where you are reads as a card lifted out of the list, rather than a stripe
 * painted behind it: a white face on the pale menu, a hairline and the same
 * small shadow a card gets. Every other row is left plain, so one thing in
 * the column is obviously the thing you are looking at.
 *
 * It is white rather than blue on purpose. A block of colour in the menu
 * competes with the one primary action on the page, which is the thing that
 * most needs to be found, and a product where four things are blue is a
 * product where nothing is.
 */
const rowClasses = (isCurrent: boolean, collapsed: boolean) => cn(
  'group relative flex w-full items-center gap-3 rounded-lg py-2 pl-2.5 pr-2.5 text-[14.5px] transition-colors',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
  isCurrent
    ? 'border border-sidebar-border bg-sidebar-primary font-semibold text-sidebar-primary-foreground shadow-[var(--shadow-raised)] dark:bg-ink-800/70 dark:text-ink-50'
    : 'border border-transparent font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground dark:text-ink-400 dark:hover:bg-ink-800/60',
  collapsed && 'justify-center px-0',
);

function FlatRow({
  to, label, icon: Icon, collapsed, current, onNavigate,
}: {
  to: string; label: string; icon: any; collapsed: boolean; current: boolean; onNavigate?: () => void;
}) {
  return (
    <li>
      <Link
        to={to}
        onClick={onNavigate}
        title={collapsed ? label : undefined}
        aria-current={current ? 'page' : undefined}
        className={rowClasses(current, collapsed)}
      >
        {Icon && (
          <Icon className={cn(
            'h-[17px] w-[17px] shrink-0 transition-colors',
            current ? 'text-sidebar-primary-foreground dark:text-ink-50' : 'text-ink-400 group-hover:text-ink-600 dark:group-hover:text-ink-200',
          )} />
        )}
        {collapsed ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
      </Link>
    </li>
  );
}

function TaskRow({
  task, collapsed, pathname, onNavigate,
}: { task: Task; collapsed: boolean; pathname: string; onNavigate?: () => void }) {
  const base = task.to.split('?')[0];
  const current = pathname === base || (base !== '/' && pathname.startsWith(`${base}/`));
  return (
    <FlatRow
      to={task.to} label={task.label} icon={navIcon(task.icon) ?? LayoutList}
      collapsed={collapsed} current={current} onNavigate={onNavigate}
    />
  );
}

/* ------------------------------------------------------------------------- */
function SidebarBrand({ collapsed, onToggleCollapsed }: { collapsed: boolean; onToggleCollapsed: () => void }) {
  return (
    <div className={cn(
      'flex h-14 shrink-0 items-center border-b border-border',
      collapsed ? 'justify-center px-2' : 'justify-between pl-4 pr-2',
    )}>
      {!collapsed && (
        <Link to="/" className="flex items-center gap-2 overflow-hidden">
          <span
            aria-hidden
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary text-[14px] font-bold text-primary-foreground"
          >
            R
          </span>
          <span className="truncate text-[15px] font-semibold tracking-[-0.01em]">RSC ERP</span>
        </Link>
      )}

      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
        className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
      </button>
    </div>
  );
}

interface RowProps {
  item: NavItem;
  collapsed: boolean;
  pathname: string;
  search: string;
  onNavigate?: () => void;
}

/** A row of the full module tree, which is unchanged from before. */
function SidebarRow({ item, collapsed, pathname, search, onNavigate }: RowProps) {
  const hasChildren = !!item.children?.length;
  const active = anyActive(item, pathname, search);
  const [open, setOpen] = useState(active);

  // Landing inside a group opens it, so the user can see where they are.
  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const Icon = navIcon(item.icon);

  if (hasChildren) {
    if (collapsed) {
      // Collapsed, a group is its icon; clicking it opens the first entry.
      const first = item.children![0];
      return (
        <li>
          <Link to={first.path!} title={item.label} onClick={onNavigate} className={rowClasses(active, collapsed)}>
            {Icon && <Icon className="h-[17px] w-[17px] shrink-0" />}
            <span className="sr-only">{item.label}</span>
          </Link>
        </li>
      );
    }

    return (
      <li>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={rowClasses(active && !open, collapsed)}
        >
          {Icon && <Icon className="h-[17px] w-[17px] shrink-0" />}
          <span className="flex-1 truncate text-left">{item.label}</span>
          <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </button>

        {open && (
          <ul className="ml-[19px] mt-0.5 space-y-0.5 border-l border-sidebar-border pb-1 pl-[14px]">
            {item.children!.map((child) => {
              // A group inside a group keeps its own heading rather than
              // losing its entries, which is what used to happen here.
              if (child.children?.length) {
                return (
                  <SidebarRow
                    key={child.path ?? child.label}
                    item={child} collapsed={false} pathname={pathname} search={search} onNavigate={onNavigate}
                  />
                );
              }
              const current = leafActive(child, pathname, search);
              return (
                <li key={child.path ?? child.label}>
                  <Link
                    to={child.path!}
                    onClick={onNavigate}
                    aria-current={current ? 'page' : undefined}
                    className={cn(
                      'block truncate rounded-md px-2.5 py-1.5 text-[15px] transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      current
                        ? 'bg-accent font-medium text-accent-foreground'
                        : 'text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground',
                    )}
                  >
                    {child.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  }

  const current = leafActive(item, pathname, search);
  return (
    <li>
      <Link
        to={item.path!}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        aria-current={current ? 'page' : undefined}
        className={rowClasses(current, collapsed)}
      >
        {Icon && <Icon className="h-[17px] w-[17px] shrink-0" />}
        {!collapsed && <span className="truncate">{item.label}</span>}
        {collapsed && <span className="sr-only">{item.label}</span>}
      </Link>
    </li>
  );
}
